import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { formatToShortNumber } from '@flipster/utils/num';

import { useProductConfig } from '../../product-config';

export const FeeTab = () => {
  const { detailsAtom } = useProductConfig();
  const details = useAtomValue(detailsAtom);

  return (
    <div className={stack({ borderTopWidth: '1px', borderTopStyle: 'solid', borderColor: 'border.default.level-1' })}>
      <div
        className={hstack({
          justify: 'space-between',
          py: 10,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderColor: 'border.default.level-1',
        })}
      >
        <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
          <FormattedMessage id='Earn.r251208.Subscription' />
        </p>
        <div className={stack({ align: 'flex-end', gap: 2 })}>
          <Formatted.Percent
            value={details?.subscriptionFee}
            isRate
            toFixed
            precision={2}
            css={{ textStyle: 'Text/M/Monospace/Strong', color: 'content.default.level-5' }}
          />
          <span className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage
              id='Earn.r260224.Redemption_processing_time'
              values={{ hour: details?.subscriptionProcessingTimeHours ?? '-' }}
            />
          </span>
        </div>
      </div>
      <div
        className={stack({
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderColor: 'border.default.level-1',
          py: 10,
          gap: 8,
        })}
      >
        <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2', py: 4 })}>
          <FormattedMessage id='Earn.r251208.Redemption' />
        </p>

        <div className={hstack({ justify: 'space-between' })}>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <FormattedMessage id='Earn.r260108.Instant' />
          </p>
          <div className={stack({ align: 'flex-end', gap: 2 })}>
            <Formatted.Percent
              value={details?.instantRedemptionFee}
              isRate
              toFixed
              precision={2}
              css={{ textStyle: 'Text/M/Monospace/Strong', color: 'content.default.level-5' }}
            />
            <span className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Earn.r260224.Cap' />:{' '}
              <FormattedMessage
                id='Earn.r250317.Product_details_instant_redemption_remaining_capacity'
                values={{
                  amount: formatToShortNumber(details?.instantRedemptionLeftCap ?? '0'),
                  currency: 'USDT',
                }}
              />
            </span>
          </div>
        </div>
        <div className={hstack({ justify: 'space-between' })}>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <FormattedMessage id='Earn.r260108.Standard' />
          </p>
          <div className={stack({ align: 'flex-end', gap: 2 })}>
            <Formatted.Percent
              value={details?.standardRedemptionFee}
              isRate
              toFixed
              precision={2}
              css={{ textStyle: 'Text/M/Monospace/Strong', color: 'content.default.level-5' }}
            />
            <span className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage
                id='Earn.r260224.Redemption_processing_time'
                values={{ hour: details?.redemptionProcessingTimeHours ?? '48' }}
              />
            </span>
          </div>
        </div>
      </div>
      <FeeRow
        label={<FormattedMessage id='Earn.r251208.Performance' />}
        value={<Formatted.Percent value={details?.performanceFee} isRate toFixed precision={2} />}
      />
      <FeeRow
        label={<FormattedMessage id='Earn.r251208.Management' />}
        value={<Formatted.Percent value={details?.managementFee} isRate toFixed precision={2} />}
      />
    </div>
  );
};

const FeeRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <div
    className={hstack({
      justify: 'space-between',
      py: 10,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderColor: 'border.default.level-1',
    })}
  >
    <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>{label}</p>
    <p className={css({ textStyle: 'Text/M/Monospace/Strong', color: 'content.default.level-5' })}>{value}</p>
  </div>
);
